/**
 * Deal Detail View
 *
 * Displays deal information with tabs for CoA, Segment, and Propcode mappings.
 */

import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, Building, Target, Columns } from "lucide-react";
import { downloadXlsx } from "@/utils/downloadXlsx";
import { Card, Loader, Button, Badge } from "@/ui/atoms";
import { EmptyState, SearchableSelect, type TSearchableOption } from "@/ui/molecules";
import type {
    TApiUser,
    TUpdateDealRequest,
    TApiOTBInfoResponse,
    TApiOTBUploadResponse,
} from "@/apis/proda_finance/types";
import { OrganismPropcodeVersions } from "@/ui/organisms/OrganismPropcodeVersions";
import { OrganismSegmentVersions } from "@/ui/organisms/OrganismSegmentVersions";
import type { TSegmentVersionActions } from "@/ui/organisms/OrganismSegmentVersions";
import {
    OrganismColumnMappings,
    type TColumnMappingView,
    type TColumnMappingActions,
} from "@/ui/organisms/OrganismColumnMappings";
import { OrganismFilteringRules, type TFilteringRule } from "@/ui/organisms/OrganismFilteringRules";
import { SEGMENT_CONFIGS, PROPCODE_REQUIRED_COLUMNS } from "@/ui/organisms/DealWizard";
import type { TSegmentNumber } from "@/ui/organisms/DealWizard";
import { routerPaths, useOpenFileDetail } from "@/ui/router";
import type { TDeal, TAppStore } from "@/store";
import { cn } from "@/utils/utils";
import { OTBUploadArea } from "./OTBUploadArea";
import { DealInfoForm } from "./DealInfoForm";

/**
 * Business Partner basic info
 */
export type TDealBusinessPartnerInfo = {
    id: string;
    name: string;
    code: string;
};

/**
 * CoA Mapping type
 */
export type TCoAMappingView = {
    id: string;
    thirdAccountCode: string;
    thirdAccountName: string;
    yardiAccountCode: string | null;
    yardiAccountName: string | null;
    level: "A" | "P" | "C";
    isInForce: boolean;
    isActive: boolean;
};

/**
 * Segment Mapping type
 */
export type TSegmentMappingView = {
    id: string;
    segmentType: number;
    sourceSegmentValue: string;
    sourceSegmentName: string | null;
    targetSegmentValue: string;
    isActive: boolean;
};

/**
 * Propcode Mapping type
 */
export type TPropcodeMappingView = {
    id: string;
    level: string;
    sourcePropcode: string;
    targetPropcode: string;
    isActive: boolean;
};

type TTabId =
    | "overview"
    | "otb"
    | "propcode"
    | "segment2"
    | "segment4"
    | "segment5"
    | "segment6"
    | "segment7"
    | "tb-column-mappings"
    | "gl-column-mappings";

const tabs: { id: TTabId; label: string; icon: typeof FileText }[] = [
    { id: "overview", label: "Deal Info", icon: FileText },
    { id: "tb-column-mappings", label: "TB Column Mappings", icon: Columns },
    { id: "gl-column-mappings", label: "GL Column Mappings", icon: Columns },
    { id: "propcode", label: "Propcode Mappings", icon: Target },
    { id: "segment2", label: "Segment 2 Mappings", icon: Building },
    { id: "segment4", label: "Segment 4 Mappings", icon: Building },
    { id: "segment5", label: "Segment 5 Mappings", icon: Building },
    { id: "segment6", label: "Segment 6 Mappings", icon: Building },
    { id: "segment7", label: "Segment 7 Mappings", icon: Building },
];

export type TViewDealDetailParams = {
    store: TAppStore;
    useCases: () => {
        onMount: (dealId: string, bpId: string) => void;
        onUnmount: () => void;
        useDealState: () => {
            data: TDeal | null;
            loading: boolean;
            error: string | null;
        };
        useBusinessPartner: () => {
            data: TDealBusinessPartnerInfo | null;
            loading: boolean;
        };
        useSegmentMappings: () => {
            data: TSegmentMappingView[] | null;
            loading: boolean;
        };
        usePropcodeMappings: () => {
            data: TPropcodeMappingView[] | null;
            loading: boolean;
        };
        /**
         * Server-side search over the company's active master property codes. Lazy
         * (per keystroke, debounced by `SearchableSelect`) rather than a preloaded
         * list, so every master code is reachable — not just the first page.
         */
        searchMasterPropertyCodes: (query: string) => Promise<TSearchableOption[]>;
        propcodeVersionActions: {
            fetchVersions: (dealId: string) => Promise<void>;
            uploadVersion: (dealId: string, file: File, description?: string) => Promise<unknown>;
            createSnapshot: (dealId: string, description?: string) => Promise<unknown>;
            restoreVersion: (dealId: string, versionId: string) => Promise<unknown>;
        };
        segmentVersionActions: TSegmentVersionActions;
        onCreatePropcodeMapping: (
            dealId: string,
            data: {
                source_property_code: string;
                target_property_code: string;
                level: string;
            },
        ) => Promise<void>;
        onUpdatePropcodeMapping: (
            dealId: string,
            mappingId: string,
            data: {
                level?: string;
                source_property_code?: string;
                target_property_code?: string;
                is_active?: boolean;
            },
        ) => Promise<void>;
        onDeletePropcodeMapping: (dealId: string, mappingId: string) => Promise<void>;
        onUpdateSegmentMapping: (
            dealId: string,
            mappingId: string,
            data: {
                source_segment_value?: string;
                source_segment_name?: string;
                target_segment_value?: string;
                target_segment_name?: string;
                is_active?: boolean;
            },
        ) => Promise<void>;
        onDeleteSegmentMapping: (dealId: string, mappingId: string) => Promise<void>;
        useMasterSegmentValues: (segmentNumber: number) => {
            data: Array<{
                code: string;
                description: string | null;
                platformValue: string | null;
            }> | null;
            loading: boolean;
        };
        onCreateSegmentMapping: (
            dealId: string,
            data: {
                segment_type: number;
                source_segment_value: string;
                source_segment_name?: string;
                target_segment_value: string;
            },
        ) => Promise<void>;
        useColumnMappings: (fileType: "tb" | "gl") => {
            data: TColumnMappingView[] | null;
            loading: boolean;
        };
        columnMappingActions: TColumnMappingActions;
        onUpdateDeal: (dealId: string, data: TUpdateDealRequest) => Promise<void>;
        onUpdateFilteringRules: (
            dealId: string,
            fileType: "tb" | "gl",
            rules: TFilteringRule[],
        ) => Promise<TFilteringRule[]>;
        useUsers: () => {
            data: TApiUser[] | null;
            loading: boolean;
        };
        useOTBInfo: () => {
            data: TApiOTBInfoResponse | null;
            loading: boolean;
        };
        onUploadOTB: (dealId: string, file: File) => Promise<TApiOTBUploadResponse>;
        onRefreshOTBInfo: (dealId: string) => Promise<void>;
        // Permission flags
        canEditDeal?: boolean;
        canEditDealName?: boolean;
        canViewColumns?: boolean;
        canEditTbColumns?: boolean;
        canEditGlColumns?: boolean;
        canUploadOtb?: boolean;
        canViewPropcode?: boolean;
        canUploadPropcode?: boolean;
        canSnapshotPropcode?: boolean;
        canRestorePropcode?: boolean;
        canEditPropcode?: boolean;
        canDeletePropcode?: boolean;
        canViewSegment?: boolean;
        canWriteSegment?: boolean;
    };
};

export type TViewDealDetailProps = Record<string, never>;

const levelLabels: Record<string, string> = {
    A: "All",
    P: "Property",
    C: "Company",
};

const levelColors: Record<string, string> = {
    A: "bg-blue-100 text-blue-700",
    P: "bg-green-100 text-green-700",
    C: "bg-purple-100 text-purple-700",
};

export const createViewDealDetail = ({ store, useCases }: TViewDealDetailParams) => {
    return function ViewDealDetail() {
        const { bpId, dealId } = useParams<{ bpId: string; dealId: string }>();
        const navigate = useNavigate();
        const openFileDetail = useOpenFileDetail();
        const {
            onMount,
            onUnmount,
            useDealState,
            useBusinessPartner,
            useSegmentMappings,
            usePropcodeMappings,
            searchMasterPropertyCodes,
            propcodeVersionActions,
            segmentVersionActions,
            onCreatePropcodeMapping,
            onUpdatePropcodeMapping,
            onDeletePropcodeMapping,
            onUpdateSegmentMapping,
            onDeleteSegmentMapping,
            useMasterSegmentValues,
            onCreateSegmentMapping,
            useColumnMappings,
            columnMappingActions,
            onUpdateDeal,
            onUpdateFilteringRules,
            useUsers,
            useOTBInfo,
            onUploadOTB,
            onRefreshOTBInfo,
            canEditDeal = true,
            canEditDealName = false,
            canViewColumns = true,
            canEditTbColumns = true,
            canEditGlColumns = true,
            canUploadOtb = true,
            canViewPropcode = true,
            canUploadPropcode = true,
            canSnapshotPropcode = true,
            canRestorePropcode = true,
            canEditPropcode = true,
            canDeletePropcode = true,
            canViewSegment = true,
            canWriteSegment = true,
        } = useCases();

        const dealState = useDealState();
        const businessPartner = useBusinessPartner();
        const usersState = useUsers();
        const segmentMappings = useSegmentMappings();
        const propcodeMappings = usePropcodeMappings();
        const masterSegment4 = useMasterSegmentValues(4);
        const masterSegment5 = useMasterSegmentValues(5);
        const masterSegment6 = useMasterSegmentValues(6);
        const masterSegment7 = useMasterSegmentValues(7);
        const tbColumnMappings = useColumnMappings("tb");
        const glColumnMappings = useColumnMappings("gl");
        const otbInfo = useOTBInfo();

        // Filtering rules state (edit detail). Saving / error / saved are keyed by file
        // type so a GL failure does not render on the TB tab and vice versa.
        const [tbFilteringRules, setTbFilteringRules] = useState<TFilteringRule[]>([]);
        const [glFilteringRules, setGlFilteringRules] = useState<TFilteringRule[]>([]);
        const [filteringRulesSavingFor, setFilteringRulesSavingFor] = useState<"tb" | "gl" | null>(
            null,
        );
        const [filteringRulesSaveErrors, setFilteringRulesSaveErrors] = useState<
            Partial<Record<"tb" | "gl", string>>
        >({});
        const [filteringRulesSavedFor, setFilteringRulesSavedFor] = useState<"tb" | "gl" | null>(
            null,
        );
        const [glTypeSaving, setGlTypeSaving] = useState(false);
        const [glTypeSaveError, setGlTypeSaveError] = useState<string | null>(null);

        const [activeTab, setActiveTab] = useState<TTabId>("overview");
        const [isCreatingPropcode, setIsCreatingPropcode] = useState(false);
        const [createPropcodeDraft, setCreatePropcodeDraft] = useState({
            sourcePropcode: "",
            targetPropcode: "",
            level: "A",
        });
        const [createPropcodeError, setCreatePropcodeError] = useState<string | null>(null);
        const [editingPropcodeId, setEditingPropcodeId] = useState<string | null>(null);
        const [editingPropcodeDraft, setEditingPropcodeDraft] = useState<{
            level: string;
            sourcePropcode: string;
            targetPropcode: string;
        } | null>(null);
        const [deletingPropcodeId, setDeletingPropcodeId] = useState<string | null>(null);
        const [propcodeEditError, setPropcodeEditError] = useState<string | null>(null);
        const [editingSegmentMappingId, setEditingSegmentMappingId] = useState<string | null>(null);
        const [editingSegmentMappingDraft, setEditingSegmentMappingDraft] = useState<{
            sourceSegmentValue: string;
            sourceSegmentName: string;
            targetSegmentValue: string;
        } | null>(null);
        const [deletingSegmentMappingId, setDeletingSegmentMappingId] = useState<string | null>(
            null,
        );
        const [isCreatingSegment, setIsCreatingSegment] = useState(false);
        const [createSegmentDraft, setCreateSegmentDraft] = useState({
            sourceSegmentValue: "",
            sourceSegmentName: "",
            targetSegmentValue: "",
        });
        const [createSegmentError, setCreateSegmentError] = useState<string | null>(null);
        const [creatingSegmentType, setCreatingSegmentType] = useState<number | null>(null);

        useEffect(() => {
            setActiveTab("overview");
            if (dealId && bpId) {
                void onMount(dealId, bpId);
            }
            return onUnmount;
        }, [dealId, bpId, onMount, onUnmount]);

        // Sync filtering rules from deal state when the deal identity changes
        useEffect(() => {
            if (dealState.data) {
                setTbFilteringRules(dealState.data.tbFilteringRules ?? []);
                setGlFilteringRules(dealState.data.glFilteringRules ?? []);
            }
        }, [dealState.data?.id]);

        const handleBackToDeals = useCallback(() => {
            if (bpId) {
                navigate(routerPaths.admin.businessPartnerDeals(bpId));
            }
        }, [bpId, navigate]);

        // Loading state
        if (dealState.loading || businessPartner.loading) {
            return (
                <div className="flex items-center justify-center py-12">
                    <Loader size="lg" />
                </div>
            );
        }

        // Error state
        if (dealState.error) {
            return (
                <div className="space-y-6">
                    <div className="text-center py-8">
                        <p className="text-destructive">{dealState.error}</p>
                        <Button variant="outline" onClick={handleBackToDeals} className="mt-4">
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            Back to Deals
                        </Button>
                    </div>
                </div>
            );
        }

        const deal = dealState.data;
        if (!deal) {
            return (
                <EmptyState
                    variant="error"
                    title="Deal not found"
                    description="The requested deal could not be found."
                    action={{ label: "Back to Deals", onClick: handleBackToDeals }}
                />
            );
        }

        const dealFileTypeLabels: Record<string, string> = {
            tb_and_gl: "TB and GL",
            tb_only: "TB Only",
        };

        const finalDestinationOptions: { value: string; label: string }[] = [
            { value: "yardi", label: "Platform" },
            { value: "data_platform", label: "Data Platform" },
        ];

        const segment2MappingTypes: { value: string; label: string }[] = [
            { value: "no_mapping", label: "No Mapping" },
            { value: "source_code_mapping", label: "Source Code Mapping" },
        ];

        const segment4MappingTypes: { value: string; label: string }[] = [
            { value: "no_mapping", label: "No Mapping" },
            { value: "source_inter_company_mapping", label: "Source Inter-Company Mapping" },
            { value: "sub_ledger", label: "Sub Ledger" },
        ];

        const segment5MappingTypes: { value: string; label: string }[] = [
            { value: "no_mapping", label: "No Mapping" },
            { value: "source_account_mapping", label: "Source Account Mapping" },
        ];

        const segment6MappingTypes: { value: string; label: string }[] = [
            { value: "no_mapping", label: "No Mapping" },
            { value: "source_account_mapping", label: "Source Account Mapping" },
        ];

        const segment7MappingTypes: { value: string; label: string }[] = [
            { value: "no_mapping", label: "No Mapping" },
            { value: "source_account_mapping", label: "Source Account Mapping" },
        ];

        const formatStartPeriod = (startPeriod: string | null): string => {
            if (!startPeriod) return "Not set";
            const date = new Date(startPeriod);
            const months = [
                "January",
                "February",
                "March",
                "April",
                "May",
                "June",
                "July",
                "August",
                "September",
                "October",
                "November",
                "December",
            ];
            return `${months[date.getMonth()]} ${date.getFullYear()}`;
        };

        const renderOverviewTab = () => (
            <DealInfoForm
                deal={deal}
                users={usersState.data ?? []}
                usersLoading={usersState.loading}
                finalDestinationOptions={finalDestinationOptions}
                segment2MappingTypes={segment2MappingTypes}
                segment4MappingTypes={segment4MappingTypes}
                segment5MappingTypes={segment5MappingTypes}
                segment6MappingTypes={segment6MappingTypes}
                segment7MappingTypes={segment7MappingTypes}
                dealFileTypeLabels={dealFileTypeLabels}
                formatStartPeriod={formatStartPeriod}
                canEdit={canEditDeal}
                canEditName={canEditDealName}
                onSave={async (data) => {
                    if (!dealId) return;
                    await onUpdateDeal(dealId, data);
                }}
            />
        );

        const getSegmentMappingType = (segmentType: number): string | null => {
            if (!deal) return null;
            switch (segmentType) {
                case 2:
                    return deal.segment2MappingType;
                case 4:
                    return deal.segment4MappingType;
                case 5:
                    return deal.segment5MappingType;
                case 6:
                    return deal.segment6MappingType;
                case 7:
                    return deal.segment7MappingType;
                default:
                    return null;
            }
        };

        const handleDownloadSegmentMappings = (segmentType: number) => {
            const filtered =
                segmentMappings.data?.filter((m) => m.segmentType === segmentType) ?? [];
            if (filtered.length === 0) return;
            const config = SEGMENT_CONFIGS[segmentType as TSegmentNumber];
            if (!config) return;
            // .xlsx with the same headers, values and sheet name the upload parser
            // expects (and the backend snapshot export writes), so the download can be
            // edited and re-uploaded directly. Segment upload only accepts .xlsx.
            const rows = filtered.map((m) => ({
                "Source Counterparty Code": m.sourceSegmentValue,
                "Source Counterparty Name": m.sourceSegmentName || "",
                [config.targetColumnName]: m.targetSegmentValue,
            }));
            downloadXlsx(
                `segment-${segmentType}-mappings-${deal.code}`,
                config.requiredColumns,
                rows,
                `Segment ${segmentType} Mappings`,
            );
        };

        const handleCreateSegmentMapping = async (segmentType: number) => {
            if (!dealId) return;
            setCreateSegmentError(null);
            try {
                await onCreateSegmentMapping(dealId, {
                    segment_type: segmentType,
                    source_segment_value: createSegmentDraft.sourceSegmentValue,
                    source_segment_name: createSegmentDraft.sourceSegmentName || undefined,
                    target_segment_value: createSegmentDraft.targetSegmentValue,
                });
                if (bpId) onMount(dealId, bpId);
                setIsCreatingSegment(false);
                setCreatingSegmentType(null);
                setCreateSegmentDraft({
                    sourceSegmentValue: "",
                    sourceSegmentName: "",
                    targetSegmentValue: "",
                });
            } catch (err) {
                const message =
                    err instanceof Error ? err.message : "Failed to create segment mapping";
                setCreateSegmentError(message);
            }
        };

        const masterSegmentValuesMap: Record<
            number,
            Array<{ code: string; description: string | null; platformValue: string | null }>
        > = {
            4: masterSegment4.data ?? [],
            5: masterSegment5.data ?? [],
            6: masterSegment6.data ?? [],
            7: masterSegment7.data ?? [],
        };

        const renderTargetValueField = (
            segmentType: number,
            value: string,
            onChange: (val: string) => void,
            className: string,
        ) => {
            if (segmentType >= 4) {
                const options = masterSegmentValuesMap[segmentType] ?? [];
                return (
                    <select
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        className={className}
                    >
                        <option value="">Select...</option>
                        {options
                            .filter((opt) => opt.platformValue)
                            .map((opt) => (
                                <option key={opt.platformValue} value={opt.platformValue!}>
                                    {opt.platformValue}
                                </option>
                            ))}
                    </select>
                );
            }
            return (
                <input
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className={className}
                />
            );
        };

        const renderSegmentTypeTab = (segmentType: number) => {
            const mappingType = getSegmentMappingType(segmentType);

            // Empty state: if segment behavior is NO_MAPPING
            if (mappingType === "no_mapping") {
                return (
                    <Card className="p-6">
                        <p className="text-muted-foreground text-center py-8">
                            Segment {segmentType} mappings are not configured for this deal.
                        </p>
                    </Card>
                );
            }

            // Filter segment mappings by type
            const filteredMappings =
                segmentMappings.data?.filter((m) => m.segmentType === segmentType) ?? [];
            const isCreatingThisSegment = isCreatingSegment && creatingSegmentType === segmentType;

            return (
                <div className="space-y-6">
                    {/* Version Management */}
                    {dealId && segmentVersionActions && (
                        <OrganismSegmentVersions
                            dealId={dealId}
                            segmentType={segmentType}
                            store={store}
                            segmentVersionActions={segmentVersionActions}
                            onVersionChange={() => {
                                if (dealId && bpId) {
                                    onMount(dealId, bpId);
                                }
                            }}
                            canUpload={canWriteSegment}
                            canSnapshot={canWriteSegment}
                            canRestore={canWriteSegment}
                        />
                    )}

                    {/* Error messages */}
                    {createSegmentError && creatingSegmentType === segmentType && (
                        <div
                            className="rounded-md bg-destructive/10 border border-destructive/30 px-4 py-3 text-sm text-destructive"
                            role="alert"
                        >
                            {createSegmentError}
                        </div>
                    )}

                    {/* Mappings Table */}
                    <Card className="p-6">
                        {/* Action buttons */}
                        <div className="flex gap-2 items-center justify-end mb-4">
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDownloadSegmentMappings(segmentType)}
                                disabled={filteredMappings.length === 0}
                            >
                                Download
                            </Button>
                            {canWriteSegment && (
                                <Button
                                    size="sm"
                                    variant="default"
                                    onClick={() => {
                                        setIsCreatingSegment(true);
                                        setCreatingSegmentType(segmentType);
                                        setCreateSegmentError(null);
                                        setCreateSegmentDraft({
                                            sourceSegmentValue: "",
                                            sourceSegmentName: "",
                                            targetSegmentValue: "",
                                        });
                                    }}
                                    disabled={isCreatingThisSegment}
                                >
                                    Add new
                                </Button>
                            )}
                        </div>
                        {segmentMappings.loading ? (
                            <div className="flex justify-center py-8">
                                <Loader size="md" />
                            </div>
                        ) : isCreatingThisSegment || filteredMappings.length > 0 ? (
                            <div className="border rounded-lg overflow-hidden">
                                <div className="max-h-96 overflow-y-auto">
                                    <table className="w-full text-sm">
                                        <thead className="bg-muted sticky top-0">
                                            <tr>
                                                <th className="px-4 py-3 text-left font-medium">
                                                    Source Value
                                                </th>
                                                <th className="px-4 py-3 text-left font-medium">
                                                    Source Name
                                                </th>
                                                <th className="px-4 py-3 text-left font-medium">
                                                    Target Value
                                                </th>
                                                <th className="px-4 py-3 text-left font-medium">
                                                    Status
                                                </th>
                                                <th className="px-4 py-3 text-right font-medium w-28">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y">
                                            {/* Inline create row */}
                                            {isCreatingThisSegment && (
                                                <tr className="bg-blue-50">
                                                    <td className="px-4 py-3">
                                                        <input
                                                            value={
                                                                createSegmentDraft.sourceSegmentValue
                                                            }
                                                            onChange={(e) =>
                                                                setCreateSegmentDraft((p) => ({
                                                                    ...p,
                                                                    sourceSegmentValue:
                                                                        e.target.value,
                                                                }))
                                                            }
                                                            placeholder="Source value"
                                                            className="border rounded px-2 py-1 w-full max-w-[120px] font-mono text-xs"
                                                        />
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <input
                                                            value={
                                                                createSegmentDraft.sourceSegmentName
                                                            }
                                                            onChange={(e) =>
                                                                setCreateSegmentDraft((p) => ({
                                                                    ...p,
                                                                    sourceSegmentName:
                                                                        e.target.value,
                                                                }))
                                                            }
                                                            placeholder="Source name"
                                                            className="border rounded px-2 py-1 w-full max-w-[160px] text-xs"
                                                        />
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        {renderTargetValueField(
                                                            segmentType,
                                                            createSegmentDraft.targetSegmentValue,
                                                            (val) =>
                                                                setCreateSegmentDraft((p) => ({
                                                                    ...p,
                                                                    targetSegmentValue: val,
                                                                })),
                                                            "border rounded px-2 py-1 w-full max-w-[120px] font-mono text-xs",
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <Badge variant="success">Active</Badge>
                                                    </td>
                                                    <td className="px-4 py-3 text-right">
                                                        <span className="flex gap-1 justify-end">
                                                            <Button
                                                                size="sm"
                                                                variant="default"
                                                                onClick={() =>
                                                                    handleCreateSegmentMapping(
                                                                        segmentType,
                                                                    )
                                                                }
                                                                disabled={
                                                                    !createSegmentDraft.sourceSegmentValue ||
                                                                    !createSegmentDraft.targetSegmentValue
                                                                }
                                                            >
                                                                Save
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={() => {
                                                                    setIsCreatingSegment(false);
                                                                    setCreatingSegmentType(null);
                                                                    setCreateSegmentError(null);
                                                                }}
                                                            >
                                                                Cancel
                                                            </Button>
                                                        </span>
                                                    </td>
                                                </tr>
                                            )}
                                            {filteredMappings.map((mapping) => {
                                                const isEditing =
                                                    editingSegmentMappingId === mapping.id;
                                                const draft =
                                                    isEditing && editingSegmentMappingDraft
                                                        ? editingSegmentMappingDraft
                                                        : {
                                                              sourceSegmentValue:
                                                                  mapping.sourceSegmentValue,
                                                              sourceSegmentName:
                                                                  mapping.sourceSegmentName || "",
                                                              targetSegmentValue:
                                                                  mapping.targetSegmentValue,
                                                          };
                                                const isDeleting =
                                                    deletingSegmentMappingId === mapping.id;
                                                return (
                                                    <tr
                                                        key={mapping.id}
                                                        className="hover:bg-muted/50 even:bg-muted/40"
                                                    >
                                                        <td className="px-4 py-3 font-mono text-xs">
                                                            {isEditing ? (
                                                                <input
                                                                    value={draft.sourceSegmentValue}
                                                                    onChange={(e) =>
                                                                        setEditingSegmentMappingDraft(
                                                                            (p) =>
                                                                                p
                                                                                    ? {
                                                                                          ...p,
                                                                                          sourceSegmentValue:
                                                                                              e
                                                                                                  .target
                                                                                                  .value,
                                                                                      }
                                                                                    : null,
                                                                        )
                                                                    }
                                                                    className="border rounded px-2 py-1 w-full max-w-[120px] font-mono text-xs"
                                                                />
                                                            ) : (
                                                                mapping.sourceSegmentValue
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            {isEditing ? (
                                                                <input
                                                                    value={draft.sourceSegmentName}
                                                                    onChange={(e) =>
                                                                        setEditingSegmentMappingDraft(
                                                                            (p) =>
                                                                                p
                                                                                    ? {
                                                                                          ...p,
                                                                                          sourceSegmentName:
                                                                                              e
                                                                                                  .target
                                                                                                  .value,
                                                                                      }
                                                                                    : null,
                                                                        )
                                                                    }
                                                                    className="border rounded px-2 py-1 w-full max-w-[160px] text-xs"
                                                                />
                                                            ) : (
                                                                mapping.sourceSegmentName ||
                                                                "\u2014"
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3 font-mono text-xs">
                                                            {isEditing
                                                                ? renderTargetValueField(
                                                                      segmentType,
                                                                      draft.targetSegmentValue,
                                                                      (val) =>
                                                                          setEditingSegmentMappingDraft(
                                                                              (p) =>
                                                                                  p
                                                                                      ? {
                                                                                            ...p,
                                                                                            targetSegmentValue:
                                                                                                val,
                                                                                        }
                                                                                      : null,
                                                                          ),
                                                                      "border rounded px-2 py-1 w-full max-w-[120px] font-mono text-xs",
                                                                  )
                                                                : mapping.targetSegmentValue}
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <Badge
                                                                variant={
                                                                    mapping.isActive
                                                                        ? "success"
                                                                        : "outline"
                                                                }
                                                            >
                                                                {mapping.isActive
                                                                    ? "Active"
                                                                    : "Inactive"}
                                                            </Badge>
                                                        </td>
                                                        <td className="px-4 py-3 text-right">
                                                            {isEditing ? (
                                                                <span className="flex gap-1 justify-end">
                                                                    <Button
                                                                        size="sm"
                                                                        variant="default"
                                                                        onClick={async () => {
                                                                            if (
                                                                                !dealId ||
                                                                                !editingSegmentMappingDraft
                                                                            )
                                                                                return;
                                                                            try {
                                                                                await onUpdateSegmentMapping(
                                                                                    dealId,
                                                                                    mapping.id,
                                                                                    {
                                                                                        source_segment_value:
                                                                                            editingSegmentMappingDraft.sourceSegmentValue,
                                                                                        source_segment_name:
                                                                                            editingSegmentMappingDraft.sourceSegmentName,
                                                                                        target_segment_value:
                                                                                            editingSegmentMappingDraft.targetSegmentValue,
                                                                                    },
                                                                                );
                                                                                if (bpId)
                                                                                    onMount(
                                                                                        dealId,
                                                                                        bpId,
                                                                                    );
                                                                                setEditingSegmentMappingId(
                                                                                    null,
                                                                                );
                                                                                setEditingSegmentMappingDraft(
                                                                                    null,
                                                                                );
                                                                            } catch (err) {
                                                                                console.error(err);
                                                                            }
                                                                        }}
                                                                    >
                                                                        Save
                                                                    </Button>
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline"
                                                                        onClick={() => {
                                                                            setEditingSegmentMappingId(
                                                                                null,
                                                                            );
                                                                            setEditingSegmentMappingDraft(
                                                                                null,
                                                                            );
                                                                        }}
                                                                    >
                                                                        Cancel
                                                                    </Button>
                                                                </span>
                                                            ) : isDeleting ? (
                                                                <span className="flex gap-1 justify-end">
                                                                    <span className="text-xs text-muted-foreground">
                                                                        Delete?
                                                                    </span>
                                                                    <Button
                                                                        size="sm"
                                                                        variant="destructive"
                                                                        onClick={async () => {
                                                                            if (!dealId) return;
                                                                            try {
                                                                                await onDeleteSegmentMapping(
                                                                                    dealId,
                                                                                    mapping.id,
                                                                                );
                                                                                if (bpId)
                                                                                    onMount(
                                                                                        dealId,
                                                                                        bpId,
                                                                                    );
                                                                                setDeletingSegmentMappingId(
                                                                                    null,
                                                                                );
                                                                            } catch (err) {
                                                                                console.error(err);
                                                                            }
                                                                        }}
                                                                    >
                                                                        Yes
                                                                    </Button>
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline"
                                                                        onClick={() =>
                                                                            setDeletingSegmentMappingId(
                                                                                null,
                                                                            )
                                                                        }
                                                                    >
                                                                        No
                                                                    </Button>
                                                                </span>
                                                            ) : (
                                                                <span className="flex gap-1 justify-end">
                                                                    {canWriteSegment && (
                                                                        <Button
                                                                            size="sm"
                                                                            variant="ghost"
                                                                            onClick={() => {
                                                                                setEditingSegmentMappingId(
                                                                                    mapping.id,
                                                                                );
                                                                                setEditingSegmentMappingDraft(
                                                                                    {
                                                                                        sourceSegmentValue:
                                                                                            mapping.sourceSegmentValue,
                                                                                        sourceSegmentName:
                                                                                            mapping.sourceSegmentName ||
                                                                                            "",
                                                                                        targetSegmentValue:
                                                                                            mapping.targetSegmentValue,
                                                                                    },
                                                                                );
                                                                            }}
                                                                        >
                                                                            Edit
                                                                        </Button>
                                                                    )}
                                                                    {canWriteSegment && (
                                                                        <Button
                                                                            size="sm"
                                                                            variant="ghost"
                                                                            className="text-destructive hover:text-destructive"
                                                                            onClick={() =>
                                                                                setDeletingSegmentMappingId(
                                                                                    mapping.id,
                                                                                )
                                                                            }
                                                                        >
                                                                            Delete
                                                                        </Button>
                                                                    )}
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ) : (
                            <p className="text-muted-foreground text-center py-8">
                                No segment {segmentType} mappings configured for this deal. Upload
                                an Excel file above to create mappings.
                            </p>
                        )}
                    </Card>
                </div>
            );
        };

        /**
         * Seed for a Propcode dropdown. A stored mapping holds only the code, so the
         * code doubles as the resting label (as the file-review propcode fields do);
         * search results carry the fuller `CODE - Description` label.
         */
        const propcodeSeed = (code: string): TSearchableOption | null =>
            code ? { value: code, label: code } : null;

        const handleDownloadPropcodes = () => {
            if (!propcodeMappings.data || propcodeMappings.data.length === 0) return;
            // .xlsx in upload format so the download can be edited and re-uploaded
            // directly. `Level` is deliberately absent: the parser derives it from the
            // first character of Platform Entity Code rather than reading a column.
            const rows = propcodeMappings.data.map((m) => ({
                "Third Entity Code": m.sourcePropcode,
                "Platform Entity Code": m.targetPropcode,
            }));
            downloadXlsx(
                `propcode-mappings-${deal.code}`,
                PROPCODE_REQUIRED_COLUMNS,
                rows,
                "Propcode Mappings",
            );
        };

        const handleCreatePropcode = async () => {
            if (!dealId) return;
            setCreatePropcodeError(null);
            try {
                await onCreatePropcodeMapping(dealId, {
                    source_property_code: createPropcodeDraft.sourcePropcode,
                    target_property_code: createPropcodeDraft.targetPropcode,
                    level: createPropcodeDraft.level,
                });
                if (bpId) onMount(dealId, bpId);
                setIsCreatingPropcode(false);
                setCreatePropcodeDraft({ sourcePropcode: "", targetPropcode: "", level: "A" });
            } catch (err) {
                const message =
                    err instanceof Error ? err.message : "Failed to create propcode mapping";
                setCreatePropcodeError(message);
            }
        };

        const renderPropcodeTab = () => (
            <div className="space-y-6">
                {/* Version Management */}
                {dealId && propcodeVersionActions && (
                    <OrganismPropcodeVersions
                        dealId={dealId}
                        store={store}
                        propcodeVersionActions={propcodeVersionActions}
                        onVersionChange={() => {
                            if (dealId && bpId) {
                                onMount(dealId, bpId);
                            }
                        }}
                        canUpload={canUploadPropcode}
                        canSnapshot={canSnapshotPropcode}
                        canRestore={canRestorePropcode}
                    />
                )}

                {/* Error messages */}
                {propcodeEditError && (
                    <div
                        className="rounded-md bg-destructive/10 border border-destructive/30 px-4 py-3 text-sm text-destructive"
                        role="alert"
                    >
                        {propcodeEditError}
                    </div>
                )}
                {createPropcodeError && (
                    <div
                        className="rounded-md bg-destructive/10 border border-destructive/30 px-4 py-3 text-sm text-destructive"
                        role="alert"
                    >
                        {createPropcodeError}
                    </div>
                )}

                {/* Mappings Table */}
                <Card className="p-6">
                    {/* Action buttons */}
                    <div className="flex gap-2 items-center justify-end mb-4">
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={handleDownloadPropcodes}
                            disabled={!propcodeMappings.data || propcodeMappings.data.length === 0}
                        >
                            Download
                        </Button>
                        {canEditPropcode && (
                            <Button
                                size="sm"
                                variant="default"
                                onClick={() => {
                                    setIsCreatingPropcode(true);
                                    setCreatePropcodeError(null);
                                    setCreatePropcodeDraft({
                                        sourcePropcode: "",
                                        targetPropcode: "",
                                        level: "A",
                                    });
                                }}
                                disabled={isCreatingPropcode}
                            >
                                Add new
                            </Button>
                        )}
                    </div>
                    {propcodeMappings.loading ? (
                        <div className="flex justify-center py-8">
                            <Loader size="md" />
                        </div>
                    ) : isCreatingPropcode ||
                      (propcodeMappings.data && propcodeMappings.data.length > 0) ? (
                        <div className="border rounded-lg overflow-hidden">
                            <div className="max-h-96 overflow-y-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-muted sticky top-0">
                                        <tr>
                                            <th className="px-4 py-3 text-left font-medium">
                                                Level
                                            </th>
                                            <th className="px-4 py-3 text-left font-medium">
                                                Source Propcode
                                            </th>
                                            <th className="px-4 py-3 text-left font-medium">
                                                Propcode
                                            </th>
                                            <th className="px-4 py-3 text-left font-medium">
                                                Status
                                            </th>
                                            <th className="px-4 py-3 text-right font-medium w-28">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y">
                                        {/* Create row */}
                                        {isCreatingPropcode && (
                                            <tr className="bg-blue-50/50">
                                                <td className="px-4 py-3">
                                                    <select
                                                        value={createPropcodeDraft.level}
                                                        onChange={(e) =>
                                                            setCreatePropcodeDraft((p) => ({
                                                                ...p,
                                                                level: e.target.value,
                                                            }))
                                                        }
                                                        className="w-12 border rounded px-1 py-1 text-xs"
                                                    >
                                                        {(["A", "P", "C"] as const).map((l) => (
                                                            <option key={l} value={l}>
                                                                {l}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <input
                                                        value={createPropcodeDraft.sourcePropcode}
                                                        onChange={(e) =>
                                                            setCreatePropcodeDraft((p) => ({
                                                                ...p,
                                                                sourcePropcode: e.target.value,
                                                            }))
                                                        }
                                                        placeholder="Source propcode"
                                                        className="border rounded px-2 py-1 w-full max-w-[160px] font-mono text-xs"
                                                    />
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="w-full max-w-[240px]">
                                                        <SearchableSelect
                                                            searchOnOpen
                                                            value={propcodeSeed(
                                                                createPropcodeDraft.targetPropcode,
                                                            )}
                                                            onSearch={searchMasterPropertyCodes}
                                                            onSelect={(option) =>
                                                                setCreatePropcodeDraft((p) => ({
                                                                    ...p,
                                                                    targetPropcode: option
                                                                        ? option.value
                                                                        : "",
                                                                }))
                                                            }
                                                            placeholder="Search propcode…"
                                                        />
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <Badge variant="success">Active</Badge>
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    <span className="flex gap-1 justify-end">
                                                        <Button
                                                            size="sm"
                                                            variant="default"
                                                            disabled={
                                                                !createPropcodeDraft.sourcePropcode ||
                                                                !createPropcodeDraft.targetPropcode
                                                            }
                                                            onClick={handleCreatePropcode}
                                                        >
                                                            Save
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => {
                                                                setIsCreatingPropcode(false);
                                                                setCreatePropcodeError(null);
                                                            }}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </span>
                                                </td>
                                            </tr>
                                        )}
                                        {propcodeMappings.data?.map((mapping) => {
                                            const isEditing = editingPropcodeId === mapping.id;
                                            const draft =
                                                isEditing && editingPropcodeDraft
                                                    ? editingPropcodeDraft
                                                    : {
                                                          level: mapping.level,
                                                          sourcePropcode: mapping.sourcePropcode,
                                                          targetPropcode: mapping.targetPropcode,
                                                      };
                                            const isDeleting = deletingPropcodeId === mapping.id;
                                            return (
                                                <tr
                                                    key={mapping.id}
                                                    className="hover:bg-muted/50 even:bg-muted/40"
                                                >
                                                    <td className="px-4 py-3">
                                                        {isEditing ? (
                                                            <select
                                                                value={draft.level}
                                                                onChange={(e) =>
                                                                    setEditingPropcodeDraft((p) =>
                                                                        p
                                                                            ? {
                                                                                  ...p,
                                                                                  level: e.target
                                                                                      .value,
                                                                              }
                                                                            : null,
                                                                    )
                                                                }
                                                                className="w-12 border rounded px-1 py-1 text-xs"
                                                            >
                                                                {(["A", "P", "C"] as const).map(
                                                                    (l) => (
                                                                        <option key={l} value={l}>
                                                                            {l}
                                                                        </option>
                                                                    ),
                                                                )}
                                                            </select>
                                                        ) : (
                                                            <span
                                                                className={cn(
                                                                    "inline-flex items-center justify-center w-6 h-6 rounded text-xs font-medium",
                                                                    levelColors[mapping.level] ||
                                                                        "bg-gray-100 text-gray-700",
                                                                )}
                                                                title={
                                                                    levelLabels[mapping.level] ||
                                                                    mapping.level
                                                                }
                                                            >
                                                                {mapping.level}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 font-mono text-xs">
                                                        {isEditing ? (
                                                            <input
                                                                value={draft.sourcePropcode}
                                                                onChange={(e) =>
                                                                    setEditingPropcodeDraft((p) =>
                                                                        p
                                                                            ? {
                                                                                  ...p,
                                                                                  sourcePropcode:
                                                                                      e.target
                                                                                          .value,
                                                                              }
                                                                            : null,
                                                                    )
                                                                }
                                                                className="border rounded px-2 py-1 w-full max-w-[120px] font-mono text-xs"
                                                            />
                                                        ) : (
                                                            mapping.sourcePropcode
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 font-mono text-xs">
                                                        {isEditing ? (
                                                            <div className="w-full max-w-[240px]">
                                                                <SearchableSelect
                                                                    searchOnOpen
                                                                    value={propcodeSeed(
                                                                        draft.targetPropcode,
                                                                    )}
                                                                    onSearch={
                                                                        searchMasterPropertyCodes
                                                                    }
                                                                    onSelect={(option) =>
                                                                        setEditingPropcodeDraft(
                                                                            (p) =>
                                                                                p
                                                                                    ? {
                                                                                          ...p,
                                                                                          targetPropcode:
                                                                                              option
                                                                                                  ? option.value
                                                                                                  : "",
                                                                                      }
                                                                                    : null,
                                                                        )
                                                                    }
                                                                    placeholder="Search propcode…"
                                                                />
                                                            </div>
                                                        ) : (
                                                            mapping.targetPropcode
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <Badge
                                                            variant={
                                                                mapping.isActive
                                                                    ? "success"
                                                                    : "outline"
                                                            }
                                                        >
                                                            {mapping.isActive
                                                                ? "Active"
                                                                : "Inactive"}
                                                        </Badge>
                                                    </td>
                                                    <td className="px-4 py-3 text-right">
                                                        {isEditing ? (
                                                            <span className="flex gap-1 justify-end">
                                                                <Button
                                                                    size="sm"
                                                                    variant="default"
                                                                    onClick={async () => {
                                                                        if (
                                                                            !dealId ||
                                                                            !editingPropcodeDraft
                                                                        )
                                                                            return;
                                                                        setPropcodeEditError(null);
                                                                        try {
                                                                            await onUpdatePropcodeMapping(
                                                                                dealId,
                                                                                mapping.id,
                                                                                {
                                                                                    level: editingPropcodeDraft.level,
                                                                                    source_property_code:
                                                                                        editingPropcodeDraft.sourcePropcode,
                                                                                    target_property_code:
                                                                                        editingPropcodeDraft.targetPropcode,
                                                                                },
                                                                            );
                                                                            if (bpId)
                                                                                onMount(
                                                                                    dealId,
                                                                                    bpId,
                                                                                );
                                                                            setEditingPropcodeId(
                                                                                null,
                                                                            );
                                                                            setEditingPropcodeDraft(
                                                                                null,
                                                                            );
                                                                        } catch (err) {
                                                                            const message =
                                                                                err instanceof Error
                                                                                    ? err.message
                                                                                    : "Failed to update propcode mapping";
                                                                            setPropcodeEditError(
                                                                                message,
                                                                            );
                                                                        }
                                                                    }}
                                                                >
                                                                    Save
                                                                </Button>
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    onClick={() => {
                                                                        setEditingPropcodeId(null);
                                                                        setEditingPropcodeDraft(
                                                                            null,
                                                                        );
                                                                        setPropcodeEditError(null);
                                                                    }}
                                                                >
                                                                    Cancel
                                                                </Button>
                                                            </span>
                                                        ) : isDeleting ? (
                                                            <span className="flex gap-1 justify-end">
                                                                <span className="text-xs text-muted-foreground">
                                                                    Delete?
                                                                </span>
                                                                <Button
                                                                    size="sm"
                                                                    variant="destructive"
                                                                    onClick={async () => {
                                                                        if (!dealId) return;
                                                                        try {
                                                                            await onDeletePropcodeMapping(
                                                                                dealId,
                                                                                mapping.id,
                                                                            );
                                                                            if (bpId)
                                                                                onMount(
                                                                                    dealId,
                                                                                    bpId,
                                                                                );
                                                                            setDeletingPropcodeId(
                                                                                null,
                                                                            );
                                                                        } catch (err) {
                                                                            console.error(err);
                                                                        }
                                                                    }}
                                                                >
                                                                    Yes
                                                                </Button>
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    onClick={() =>
                                                                        setDeletingPropcodeId(null)
                                                                    }
                                                                >
                                                                    No
                                                                </Button>
                                                            </span>
                                                        ) : (
                                                            <span className="flex gap-1 justify-end">
                                                                {canEditPropcode && (
                                                                    <Button
                                                                        size="sm"
                                                                        variant="ghost"
                                                                        onClick={() => {
                                                                            setPropcodeEditError(
                                                                                null,
                                                                            );
                                                                            setEditingPropcodeId(
                                                                                mapping.id,
                                                                            );
                                                                            setEditingPropcodeDraft(
                                                                                {
                                                                                    level: mapping.level,
                                                                                    sourcePropcode:
                                                                                        mapping.sourcePropcode,
                                                                                    targetPropcode:
                                                                                        mapping.targetPropcode,
                                                                                },
                                                                            );
                                                                        }}
                                                                    >
                                                                        Edit
                                                                    </Button>
                                                                )}
                                                                {canDeletePropcode && (
                                                                    <Button
                                                                        size="sm"
                                                                        variant="ghost"
                                                                        className="text-destructive hover:text-destructive"
                                                                        onClick={() =>
                                                                            setDeletingPropcodeId(
                                                                                mapping.id,
                                                                            )
                                                                        }
                                                                    >
                                                                        Delete
                                                                    </Button>
                                                                )}
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : (
                        <p className="text-muted-foreground text-center py-8">
                            No propcode mappings configured for this deal. Upload an Excel file or
                            add a propcode mapping above.
                        </p>
                    )}
                </Card>
            </div>
        );

        const renderColumnMappingsTab = (fileType: "tb" | "gl") => {
            const canEditThisColumn = fileType === "tb" ? canEditTbColumns : canEditGlColumns;
            const mappingsState = fileType === "tb" ? tbColumnMappings : glColumnMappings;
            const filteringRules = fileType === "tb" ? tbFilteringRules : glFilteringRules;
            const setFilteringRules = fileType === "tb" ? setTbFilteringRules : setGlFilteringRules;
            const availableColumns =
                mappingsState.data && mappingsState.data.length > 0
                    ? (mappingsState.data[0]?.availableColumns ?? [])
                    : [];

            const handleChangeGlType = async (newGlType: "mtd" | "ytd") => {
                if (!dealId) return;
                setGlTypeSaving(true);
                setGlTypeSaveError(null);
                try {
                    await onUpdateDeal(dealId, { gl_type: newGlType });
                } catch (err) {
                    setGlTypeSaveError(
                        err instanceof Error ? err.message : "Failed to update GL type",
                    );
                } finally {
                    setGlTypeSaving(false);
                }
            };

            const handleSaveFilteringRules = async () => {
                if (!dealId) return;
                // A rule with no values is rejected by the backend (filtering_value has
                // min_length=1), so catch it here rather than surfacing a raw 422.
                const incomplete = filteringRules.find((r) => r.filteringValue.length === 0);
                if (incomplete) {
                    setFilteringRulesSaveErrors((prev) => ({
                        ...prev,
                        [fileType]:
                            `Rule for column "${incomplete.filteringColumnHeader}" has no values. ` +
                            "Add at least one value or remove the rule.",
                    }));
                    return;
                }
                setFilteringRulesSavingFor(fileType);
                setFilteringRulesSavedFor(null);
                setFilteringRulesSaveErrors((prev) => ({ ...prev, [fileType]: undefined }));
                try {
                    const saved = await onUpdateFilteringRules(dealId, fileType, filteringRules);
                    // Adopt the server's view so the panel cannot drift from what is stored.
                    setFilteringRules(saved);
                    setFilteringRulesSavedFor(fileType);
                } catch (err) {
                    setFilteringRulesSaveErrors((prev) => ({
                        ...prev,
                        [fileType]:
                            err instanceof Error ? err.message : "Failed to save filtering rules",
                    }));
                } finally {
                    setFilteringRulesSavingFor(null);
                }
            };

            return (
                <div className="space-y-6">
                    {fileType === "gl" && (
                        <Card className="p-6">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <h3 className="text-sm font-medium">GL Type</h3>
                                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                                        Whether the GL file received from the business partner
                                        contains Month-to-Date (MTD) or Year-to-Date (YTD) values.
                                        YTD requires a Period column mapping; for MTD the Period
                                        column is optional. Switch this if the file type received
                                        from the business partner changes.
                                    </p>
                                    {glTypeSaveError && (
                                        <p className="text-sm text-destructive mt-2">
                                            {glTypeSaveError}
                                        </p>
                                    )}
                                </div>
                                <select
                                    value={
                                        deal.glType === "ytd" || deal.glType === "mtd"
                                            ? deal.glType
                                            : ""
                                    }
                                    onChange={(e) =>
                                        void handleChangeGlType(e.target.value as "mtd" | "ytd")
                                    }
                                    disabled={!canEditDeal || glTypeSaving}
                                    className="px-3 py-2 text-sm border rounded-md bg-background shrink-0 disabled:opacity-50"
                                >
                                    <option value="" disabled>
                                        Select GL type...
                                    </option>
                                    <option value="mtd">MTD</option>
                                    <option value="ytd">YTD</option>
                                </select>
                            </div>
                        </Card>
                    )}
                    <OrganismColumnMappings
                        dealId={deal.id}
                        fileType={fileType}
                        mappings={mappingsState.data}
                        loading={mappingsState.loading}
                        deal={{
                            requiresSegment2: deal.requiresSegment2,
                            requiresSegment4: deal.requiresSegment4,
                            requiresSegment5: deal.requiresSegment5,
                            requiresSegment6: deal.requiresSegment6,
                            requiresSegment7: deal.requiresSegment7,
                            segment2MappingType: deal.segment2MappingType,
                            segment4MappingType: deal.segment4MappingType,
                            segment5MappingType: deal.segment5MappingType,
                            segment6MappingType: deal.segment6MappingType,
                            segment7MappingType: deal.segment7MappingType,
                            dealFileType: deal.dealFileType,
                            coaMappingMode: deal.coaMappingMode,
                        }}
                        actions={columnMappingActions}
                        canEdit={canEditThisColumn}
                        glType={deal.glType}
                    />
                    {availableColumns.length > 0 && (
                        <div className="border rounded-lg p-6 space-y-4">
                            <OrganismFilteringRules
                                fileType={fileType}
                                availableColumns={availableColumns}
                                rules={filteringRules}
                                onChange={setFilteringRules}
                            />
                            {filteringRulesSaveErrors[fileType] && (
                                <p className="text-sm text-destructive">
                                    {filteringRulesSaveErrors[fileType]}
                                </p>
                            )}
                            {canEditThisColumn && (
                                <div className="flex items-center justify-end gap-3">
                                    {filteringRulesSavedFor === fileType && (
                                        <p className="text-sm text-muted-foreground">
                                            Saved. Applies to files uploaded or refreshed from now
                                            on — files already processed keep their current rows.
                                        </p>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => void handleSaveFilteringRules()}
                                        disabled={filteringRulesSavingFor === fileType}
                                        className="px-4 py-2 text-sm font-medium rounded bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                                    >
                                        {filteringRulesSavingFor === fileType
                                            ? "Saving..."
                                            : "Save Filtering Rules"}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            );
        };

        const renderOTBTab = () => {
            const info = otbInfo.data;

            const statusLabels: Record<string, string> = {
                ready_for_commitment: "Ready for Commitment",
                committed: "Committed",
                rejected: "Rejected",
                under_review: "Under Review",
                under_acceptance: "Under Acceptance",
                uploaded: "Uploaded",
            };

            const statusColors: Record<string, string> = {
                ready_for_commitment: "success",
                committed: "success",
                rejected: "destructive",
                under_review: "warning",
            };

            return (
                <div className="space-y-6">
                    {otbInfo.loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader size="lg" />
                        </div>
                    ) : info?.has_otb ? (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                <div>
                                    <p className="text-sm text-muted-foreground">Status</p>
                                    <Badge
                                        variant={
                                            (statusColors[info.status ?? ""] as
                                                | "success"
                                                | "destructive"
                                                | "warning"
                                                | "outline") ?? "outline"
                                        }
                                    >
                                        {statusLabels[info.status ?? ""] ?? info.status}
                                    </Badge>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Period</p>
                                    <p className="font-medium text-sm">{info.otb_period ?? "—"}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Line Items</p>
                                    <p className="font-medium text-sm">
                                        {info.total_line_items ?? "—"}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Uploaded</p>
                                    <p className="font-medium text-sm">
                                        {info.uploaded_at
                                            ? new Date(info.uploaded_at).toLocaleDateString()
                                            : "—"}
                                    </p>
                                </div>
                            </div>

                            {info.file_id && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openFileDetail(info.file_id!)}
                                >
                                    <FileText className="h-4 w-4 mr-2" />
                                    View OTB File
                                </Button>
                            )}

                            {info.status === "rejected" && canUploadOtb && (
                                <OTBUploadArea
                                    dealId={dealId!}
                                    onUpload={onUploadOTB}
                                    onSuccess={() => {
                                        if (dealId) void onRefreshOTBInfo(dealId);
                                    }}
                                />
                            )}
                        </div>
                    ) : canUploadOtb ? (
                        <OTBUploadArea
                            dealId={dealId!}
                            onUpload={onUploadOTB}
                            onSuccess={() => {
                                if (dealId) void onRefreshOTBInfo(dealId);
                            }}
                        />
                    ) : (
                        <p className="text-sm text-muted-foreground">No OTB uploaded.</p>
                    )}
                </div>
            );
        };

        const renderActiveTab = () => {
            switch (activeTab) {
                case "overview":
                    return renderOverviewTab();
                case "otb":
                    return renderOTBTab();
                case "tb-column-mappings":
                    return renderColumnMappingsTab("tb");
                case "gl-column-mappings":
                    return renderColumnMappingsTab("gl");
                case "propcode":
                    return renderPropcodeTab();
                case "segment2":
                    return renderSegmentTypeTab(2);
                case "segment4":
                    return renderSegmentTypeTab(4);
                case "segment5":
                    return renderSegmentTypeTab(5);
                case "segment6":
                    return renderSegmentTypeTab(6);
                case "segment7":
                    return renderSegmentTypeTab(7);
                default:
                    return renderOverviewTab();
            }
        };

        return (
            <div className="space-y-6">
                {/* Header with breadcrumb */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-semibold">{deal.name}</h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            <button
                                onClick={() => navigate(routerPaths.admin.businessPartners)}
                                className="hover:text-foreground hover:underline"
                            >
                                Business Partners
                            </button>
                            {" > "}
                            <button
                                onClick={handleBackToDeals}
                                className="hover:text-foreground hover:underline"
                            >
                                {businessPartner.data?.name || "Deals"}
                            </button>
                            {" > "}
                            <span>{deal.name}</span>
                        </p>
                    </div>
                    <Button variant="outline" onClick={handleBackToDeals}>
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back to Deals
                    </Button>
                </div>

                {/* Tabs */}
                <div className="border-b">
                    <nav className="flex gap-6" aria-label="Tabs">
                        {[
                            ...tabs,
                            ...(deal.acceptsOtb
                                ? [{ id: "otb" as TTabId, label: "OTB", icon: FileText }]
                                : []),
                        ]
                            .filter((tab) => {
                                if (tab.id === "tb-column-mappings") {
                                    return canViewColumns;
                                }
                                if (tab.id === "gl-column-mappings") {
                                    return canViewColumns && deal.dealFileType === "tb_and_gl";
                                }
                                if (tab.id === "propcode") {
                                    return canViewPropcode;
                                }
                                if (
                                    tab.id === "segment2" ||
                                    tab.id === "segment4" ||
                                    tab.id === "segment5" ||
                                    tab.id === "segment6" ||
                                    tab.id === "segment7"
                                ) {
                                    return canViewSegment;
                                }
                                return true;
                            })
                            .map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={cn(
                                        "flex items-center gap-2 py-3 text-sm font-medium border-b-2 -mb-px transition-colors",
                                        activeTab === tab.id
                                            ? "border-primary text-primary"
                                            : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground",
                                    )}
                                >
                                    <tab.icon className="h-4 w-4" />
                                    {tab.label}
                                </button>
                            ))}
                    </nav>
                </div>

                {/* Tab Content */}
                {renderActiveTab()}
            </div>
        );
    };
};
